import { useCallback, useState, useRef } from "react";
import { Button } from "@/components/Button/Button";
import { Checkbox } from "@/components/form/Checkbox";
import { Input } from "@/components/form/Input";
import { Label } from "@/components/ui/label";

interface JobSearchFiltersProps {
    onSearch: (filters: {
        term: string;
        location: string;
        remote: boolean;
        page: number;
        limit: number;
    }) => void;
}

export const JobSearchFilters = ({ onSearch }: JobSearchFiltersProps) => {
    const [term, setTerm] = useState("");
    const [location, setLocation] = useState("");
    const [remote, setRemote] = useState(false);
    const suggestionsRef = useRef<HTMLDivElement>(null);

    const handleSearch = useCallback(() => {
        // Only search if both fields have content
        if (term.trim() && location.trim()) {
            onSearch({
                term: term.trim(),
                location: location.trim(),
                remote,
                page: 1,
                limit: 10,
            });
        }
    }, [onSearch, term, location, remote]);

    const handleLocationChange = (value: string) => {
        setLocation(value);
    };

    return (
        <div className="mb-2 rounded-lg border border-neutral-300 bg-white p-4 shadow-sm">
            <div className="grid w-full grid-cols-1 gap-4 md:hidden">
                <div>
                    <Label>
                        Job Title <span className="text-red-500">*</span>
                    </Label>
                    <Input
                        name="term"
                        value={term}
                        onChange={(_, value) => setTerm(value)}
                        placeholder="Enter job title or keywords"
                        minLength={2}
                    />
                </div>
                <div ref={suggestionsRef}>
                    <Label>
                        Location <span className="text-red-500">*</span>
                    </Label>
                    <Input
                        name="location"
                        value={location}
                        onChange={(_, value) => handleLocationChange(value)}
                        placeholder="Enter city, state, or remote"
                        minLength={2}
                    />
                </div>
                <div className="flex items-center gap-4">
                    <Checkbox
                        id="remote"
                        label="Remote"
                        checked={remote}
                        borderless
                        onChange={setRemote}
                    />
                    <Button
                        onClick={handleSearch}
                        color="primary"
                        className="flex-1"
                        disabled={!term.trim() || !location.trim()}
                    >
                        Search
                    </Button>
                </div>
            </div>

            {/* Desktop layout - horizontal grid */}
            <div className="hidden w-full grid-cols-8 gap-4 md:grid">
                <div className="col-span-4">
                    <div>
                        <Label>
                            Job Title <span className="text-red-500">*</span>
                        </Label>
                        <Input
                            name="term"
                            value={term}
                            onChange={(_, value) => setTerm(value)}
                            placeholder="Enter job title or keywords"
                            minLength={2}
                        />
                    </div>
                </div>
                <div
                    className="relative col-span-2"
                    ref={suggestionsRef}
                >
                    <div>
                        <Label>
                            Location <span className="text-red-500">*</span>
                        </Label>
                        <Input
                            name="location"
                            value={location}
                            onChange={(_, value) => handleLocationChange(value)}
                            placeholder="Enter city, state, or remote"
                            minLength={2}
                        />
                    </div>
                </div>
                <div className="col-span-1 flex items-end">
                    <Checkbox
                        id="remote"
                        label="Remote"
                        checked={remote}
                        borderless
                        onChange={setRemote}
                    />
                </div>
                <div className="col-span-1 flex items-end">
                    <Button
                        onClick={handleSearch}
                        color="primary"
                        className="h-10 w-full"
                        disabled={!term.trim() || !location.trim()}
                    >
                        Search
                    </Button>
                </div>
            </div>
        </div>
    );
};
